import { createSelector } from 'reselect';

import { IOneLinkEngagementCandidate } from '../../types/bucket';
import {
  isLinkContentsDigital,
  mapLinkContents,
  mapLinkEmail,
  mapLinkNote,
  mapLinkShipping,
  normalizeLinkSendOnBehalf,
  TLinkContentItem,
} from '../../utils/createSendLink';
import { selectCsClaimPageExperienceId } from '../createSend/selectors/claimPage';
import {
  selectCsBoxId,
  selectCsGiftBudget,
  selectCsIsMarketplaceCustomCart,
  selectCsItems,
  selectCsKits,
  selectCsPygOptionOrder,
} from '../createSend/selectors/contents';
import {
  selectCsAllowedDomains,
  selectCsIs2FaEnabled,
  selectCsLinkExpirationDateRaw,
  selectCsLinkOnHoldUntilRaw,
  selectCsLinkPassword,
  selectCsLinkTitleRaw,
  selectCsRedemptionLimitRaw,
  selectCsTwoFaRecipients,
} from '../createSend/selectors/linkSettings';
import {
  selectCsClaimAddressIds,
  selectCsIsGroupShipmentEnabled,
  selectCsMultiKitAddressMode,
  selectCsOverrideAddress,
} from '../createSend/selectors/recipients';
import {
  selectCsBusinessReasonId,
  selectCsDisableSenderEmails,
  selectCsInternalNotes,
  selectCsShippingOption,
  selectCsSobEmail,
} from '../createSend/selectors/sendSettings';
import { selectUserEmail } from './auth';
import {
  selectEffectiveCsCustomMessage,
  selectEffectiveCsEmail,
  selectEffectiveCsReminderInterval,
  selectEffectiveCsSendSettings,
} from './createSendFlow';

export type TCreateSendLinkCandidate = Omit<IOneLinkEngagementCandidate, 'status'> & { status?: string };

export const selectCreateSendLinkCandidate = createSelector(
  selectCsLinkTitleRaw,
  selectCsLinkExpirationDateRaw,
  selectCsLinkOnHoldUntilRaw,
  selectCsRedemptionLimitRaw,
  selectCsLinkPassword,
  selectCsAllowedDomains,
  selectCsIs2FaEnabled,
  selectCsTwoFaRecipients,
  selectCsClaimPageExperienceId,
  selectCsBusinessReasonId,
  selectCsInternalNotes,
  selectCsDisableSenderEmails,
  selectCsShippingOption,
  selectCsKits,
  selectCsItems,
  selectCsBoxId,
  selectCsMultiKitAddressMode,
  selectCsOverrideAddress,
  selectCsClaimAddressIds,
  selectEffectiveCsCustomMessage,
  selectEffectiveCsEmail,
  selectEffectiveCsReminderInterval,
  selectEffectiveCsSendSettings,
  selectCsIsGroupShipmentEnabled,
  selectCsSobEmail,
  selectUserEmail,
  selectCsGiftBudget,
  selectCsIsMarketplaceCustomCart,
  selectCsPygOptionOrder,
  (
    title,
    expirationDate,
    onHoldUntil,
    redemptionLimit,
    password,
    allowedDomains,
    is2FAEnabled,
    twoFARecipients,
    experienceId,
    businessReasonId,
    internalNotes,
    isSenderNotificationsDisabled,
    shippingOption,
    kits,
    items,
    preCreatedBoxId,
    addressMode,
    overrideAddress,
    claimAddressIds,
    customMessage,
    email,
    reminderInterval,
    effectiveSettings,
    isGroupShipmentEnabled,
    sobEmail,
    userEmail,
    giftBudget,
    isMarketplaceCustomCart,
    pygOptionOrder,
  ): TCreateSendLinkCandidate => {
    const contents = mapLinkContents(
      kits ?? [],
      (items ?? []) as TLinkContentItem[],
      preCreatedBoxId,
      { isCustomCart: isMarketplaceCustomCart, giftBudget },
      pygOptionOrder,
    );
    const isDigital = isLinkContentsDigital(contents);
    const sendOnBehalf = normalizeLinkSendOnBehalf(sobEmail, userEmail);

    return {
      title,
      password,
      ...(allowedDomains && allowedDomains.length ? { allowed_domains: allowedDomains } : {}),
      ...(expirationDate ? { expiration_date: expirationDate } : {}),
      ...(!isDigital && onHoldUntil ? { on_hold_until: onHoldUntil } : {}),
      max_submit_count: redemptionLimit,
      enabled_2fa: Boolean(is2FAEnabled),
      recipients: is2FAEnabled ? twoFARecipients?.successfulRecipients : undefined,
      ...mapLinkNote(customMessage),
      ...mapLinkEmail(email),
      ...contents,
      ...(isDigital
        ? {}
        : {
            ...mapLinkShipping(addressMode, overrideAddress, claimAddressIds, isGroupShipmentEnabled),
            shipping_option: shippingOption,
          }),
      ...(experienceId === undefined ? {} : { experience_id: experienceId }),
      internal_notes: internalNotes,
      business_reason_id: businessReasonId,
      receiver_reminder_interval: reminderInterval,
      ...(sendOnBehalf ? { send_on_behalf_the_email: sendOnBehalf } : {}),
      disable_sender_email_notifications: Boolean(isSenderNotificationsDisabled),
      disable_recipient_shipped_email: effectiveSettings.isRecipientShippedEmailDisabled,
    };
  },
);
